import { Injectable } from "@angular/core";

import { Observable, of, throwError } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";

import { DffError } from "@dffedb/util";
import { EjendomsfaktorDefinition } from "@e-forsyning/common/ejendomsfaktor";
import { Huskeliste, HuskelisteRepository } from "@e-forsyning/common/huskeliste";
import { AktuelInstallationKeyObserver } from "@e-forsyning/common/installation";
import {
    Maaler,
    MaalerOplysninger,
    MaalerRepository,
    MaalerService,
    MaalerType,
    MaalerTypeRepository,
    opretTomMaalerOplysninger
} from "@e-forsyning/common/maaler";
import {
    NoteBruger,
    NoteHenvendelsestype,
    NoteOmraade,
    NoteOmraader,
    NoteOpgavestyringStatus,
    NoteRepository
} from "@e-forsyning/common/note";

import { OpgaveListeItem } from "../opgave-liste/model/opgave-liste-item.model";
import { KomponentType } from "../opgave/arbejdsgang/komponenter/model/komponent-type.model";
import { Opgave } from "../opgave/model/opgave.model";
import { OpgavestyringIndstillinger } from "../opgave/model/opgavestyring-indstillinger.model";
import { OpgaveRepository } from "./opgave.repository";

@Injectable({ providedIn: "root" })
export class OpgaveService {
    constructor(
        private readonly repository: OpgaveRepository,
        private readonly aktuelInstallationObserver: AktuelInstallationKeyObserver,
        private readonly maalerTypeRepository: MaalerTypeRepository,
        private readonly maalerRepository: MaalerRepository,
        private readonly huskelisteRepository: HuskelisteRepository,
        private readonly noteRepository: NoteRepository,
        private readonly maalerService: MaalerService
    ) {}

    public hentMaalerOplysninger(ejendomNr: number, installationNr: number): Observable<MaalerOplysninger> {
        return installationNr === 0 ? of(opretTomMaalerOplysninger()) : this.maalerService.hentMaalerOplysninger(ejendomNr, installationNr);
    }

    public hentIndstillinger(): Observable<OpgavestyringIndstillinger> {
        return this.repository.hentIndstillinger();
    }

    public hentMaalerOplysningerForAktuelInstallation(): Observable<MaalerOplysninger> {
        return this.aktuelInstallationObserver.valueChanges.pipe(
            switchMap((i) =>
                i && i.ejendomNr ? this.maalerService.hentMaalerOplysninger(i.ejendomNr, i.installationNr) : of(opretTomMaalerOplysninger())
            )
        );
    }

    public hentOpgaver(): Observable<OpgaveListeItem[]> {
        return this.repository.hentOpgaver().pipe(catchError((error: any) => throwError(this.createError(error))));
    }

    public hentOpgave(opgaveId: number): Observable<Opgave> {
        return this.repository
            .hentOpgave(opgaveId)
            .pipe(
                catchError((error: any) =>
                    error.status === 404
                        ? throwError(
                              new DffError(error.message, { friendlyMessage: `Opgave '${opgaveId}' findes ikke.`, innerError: error })
                          )
                        : throwError(this.createError(error))
                )
            );
    }

    public hentKomponentTyper(): Observable<KomponentType[]> {
        return this.repository.hentKomponentTyper().pipe(catchError((error: any) => throwError(this.createError(error))));
    }

    public hentEjendomsfaktorDefinitioner(): Observable<EjendomsfaktorDefinition[]> {
        return this.repository.hentEjendomsfaktorDefinitioner().pipe(catchError((error: any) => throwError(this.createError(error))));
    }

    public hentMaalerePaaLager(): Observable<Maaler[]> {
        return this.maalerRepository.hentMaalerePaaLager().pipe(catchError((error: any) => throwError(this.createError(error))));
    }

    public hentMaalerTyper(): Observable<MaalerType[]> {
        return this.maalerTypeRepository.hentMaalerTyper().pipe(catchError((error: any) => throwError(this.createError(error))));
    }

    public hentHuskelister(): Observable<Huskeliste[]> {
        return this.huskelisteRepository.hentHuskelister().pipe(catchError((error: any) => throwError(this.createError(error))));
    }

    public hentNoteOmraader(): Observable<NoteOmraade[]> {
        return this.noteRepository.hentOmraader().pipe(catchError((error: any) => throwError(this.createError(error))));
    }

    public hentNoteHenvendelsestyper(): Observable<NoteHenvendelsestype[]> {
        return this.noteRepository.hentHenvendelsestyper().pipe(catchError((error: any) => throwError(this.createError(error))));
    }

    public hentNoteOpgavestyringStatusser(): Observable<NoteOpgavestyringStatus[]> {
        return this.noteRepository.hentOpgavestyringStatusser().pipe(catchError((error: any) => throwError(this.createError(error))));
    }

    public hentNoteBrugere(): Observable<NoteBruger[]> {
        return this.noteRepository.hentBrugere().pipe(catchError((error: any) => throwError(this.createError(error))));
    }

    public hentDefaultNoteOmraadeId(): number {
        return NoteOmraader.installation;
    }

    public udfoerOpgave(opgave: Opgave, onProgress: (value: number) => void): Promise<void> {
        return this.repository.udfoerOpgave(opgave, onProgress);
    }

    public hentNoteBilagUrlTemplate(): Observable<string> {
        return this.noteRepository.hentBilagUrlTemplate();
    }

    private createError(error: any): DffError {
        if (error.status === 500) {
            return new DffError(`Der er opstået en uventet fejl: ${error.message}`, error.message);
        }
        if (error.status === 0) {
            return new DffError("Det ser ud til at du mangler netværksforbindelse.", error.message);
        }
        return new DffError(error.brugerbesked || error.message, error.udviklerbesked || error.message);
    }
}
